import { Box, ButtonBase, CircularProgress } from "@mui/material";
import { grey } from "@mui/material/colors";
import { keyframes } from "@mui/system";
import { useEffect, useState } from "react";

import { trackEvent } from "utils/tracking";

const ICON_BG = "#0f3d2b";
const BADGE_BG = "#b9a230";
const BADGE_TEXT = "#3a2f00";

const CTA_BG = "#014638";
const CTA_BG_HOVER = "#165C4E";
const CTA_YELLOW = "#F7EE6F";
const CTA_SPARK = "#165C4E";
const CTA_NEW_TEXT = "#09362b";

const LOADING_MESSAGES = [
  "Finding ways to improve...",
  "Generating suggestions...",
  "Double-checking merge tags...",
  "Analyzing tone and clarity...",
  "Scoring your template...",
  "Polishing the details...",
];

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(15, 61, 43, 0.45); }
  70% { box-shadow: 0 0 0 10px rgba(15, 61, 43, 0); }
  100% { box-shadow: 0 0 0 0 rgba(15, 61, 43, 0); }
`;

interface SuggestionsChipProps {
  count?: number;
  ctaLabel?: string;
  loading?: boolean;
  onClick?: () => void;
  onNavigateUp?: () => void;
  onNavigateDown?: () => void;
}

export default function SuggestionsChip({
  count = 0,
  ctaLabel = "Get suggested edits",
  loading = false,
  onClick,
  onNavigateUp,
  onNavigateDown,
}: SuggestionsChipProps) {
  const hasSuggestions = Boolean(count && count > 0);
  const [messageIndex, setMessageIndex] = useState(0);

  const handleClick = () => {
    trackEvent("Template - Suggestion Edit Chip Clicked");
    onClick?.();
  };

  useEffect(() => {
    if (!loading) {
      setMessageIndex(0);
      return;
    }
    const id = setInterval(() => {
      setMessageIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [loading]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          pl: 0.375,
          pr: 1.25,
          py: 0.375,
          borderRadius: 999,
          border: "1px solid",
          borderColor: grey[200],
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: ICON_BG,
            color: "#fff",
            flexShrink: 0,
          }}
        >
          <CircularProgress size={12} thickness={5} sx={{ color: "#fff" }} />
        </Box>
        <Box
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: "text.primary",
            whiteSpace: "nowrap",
          }}
        >
          {LOADING_MESSAGES[messageIndex]}
        </Box>
      </Box>
    );
  }

  if (!hasSuggestions) {
    return (
      <ButtonBase
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          pl: 0.5,
          pr: 1.25,
          py: 0.625,
          borderRadius: 999,
          bgcolor: CTA_BG,
          color: CTA_YELLOW,
          fontWeight: 600,
          boxShadow: "0 4px 14px rgba(1, 70, 56, 0.2)",
          animation: `${pulse} 2.6s ease-out infinite`,
          transition: "background 0.14s, transform 0.12s, box-shadow 0.14s",
          "&:hover": {
            bgcolor: CTA_BG_HOVER,
            transform: "translateY(-1px)",
            boxShadow: "0 7px 18px rgba(1, 70, 56, 0.26)",
          },
          "&:active": { transform: "translateY(0)" },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            borderRadius: "50%",
            bgcolor: CTA_YELLOW,
            color: CTA_SPARK,
            flexShrink: 0,
          }}
        >
          <Box
            component="i"
            className="fa-solid fa-sparkles"
            sx={{ fontSize: 11 }}
          />
        </Box>
        <Box sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}>{ctaLabel}</Box>
        <Box
          sx={{
            bgcolor: CTA_YELLOW,
            color: CTA_NEW_TEXT,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            borderRadius: 999,
            px: 0.875,
            py: 0.25,
          }}
        >
          New
        </Box>
      </ButtonBase>
    );
  }

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        pl: 0.375,
        pr: 0.375,
        py: 0.375,
        borderRadius: 999,
        border: "1px solid",
        borderColor: grey[200],
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          borderRadius: 999,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: ICON_BG,
            color: "#fff",
            flexShrink: 0,
          }}
        >
          <Box
            component="i"
            className="fa-solid fa-sparkles"
            sx={{ fontSize: 11 }}
          />
        </Box>
        <Box
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: "text.primary",
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 20,
              height: 20,
              px: 0.625,
              borderRadius: "50%",
              bgcolor: BADGE_BG,
              color: BADGE_TEXT,
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            {count}
          </Box>{" "}
          improvement{count === 1 ? "" : "s"}
        </Box>
      </Box>
      <ButtonBase
        onClick={onNavigateUp}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "1px solid",
          borderColor: grey[200],
          color: grey[700],
          "&:hover": { bgcolor: grey[50] },
        }}
      >
        <Box
          component="i"
          className="fa-regular fa-chevron-up"
          sx={{ fontSize: 10 }}
        />
      </ButtonBase>
      <ButtonBase
        onClick={onNavigateDown}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "1px solid",
          borderColor: grey[200],
          color: grey[700],
          "&:hover": { bgcolor: grey[50] },
        }}
      >
        <Box
          component="i"
          className="fa-regular fa-chevron-down"
          sx={{ fontSize: 10 }}
        />
      </ButtonBase>
    </Box>
  );
}
